/**
 * Tax Flow — Shared UI Component Library (Build 004)
 *
 * Reusable, presentational building blocks used across every module so future
 * pages never re-implement layout primitives. Wraps the shadcn/ui base where
 * helpful. Import from "@/components/shared".
 */
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

export function resolveIcon(name, fallback = "Circle") {
  if (!name) return Icons[fallback];
  // accept "file-text" or "FileText"
  const pascal = name.includes("-")
    ? name.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")
    : name;
  return Icons[pascal] || Icons[name] || Icons[fallback];
}

/* ------------------------------------------------------------------ Buttons */
const BTN_VARIANTS = {
  primary: "bg-charcoal hover:bg-graphite text-white shadow-sm hover:shadow-md",
  secondary: "bg-white border border-slate-300 text-charcoal hover:bg-slate-50 hover:border-brand-gold",
  ghost: "text-slate-600 hover:bg-slate-100",
  danger: "bg-red-600 hover:bg-red-700 text-white",
  gold: "bg-brand-gold hover:bg-brand-gold-dark text-charcoal shadow-sm hover:shadow-md",
};
export function ActionButton({ icon, children, variant = "primary", className, disabled, loading, ...props }) {
  const Icon = icon ? resolveIcon(icon) : null;
  return (
    <button
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-brand-gold/50",
        BTN_VARIANTS[variant], className
      )}
      {...props}
    >
      {loading ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : Icon && <Icon className="h-4 w-4" />}
      {children}
    </button>
  );
}

/* -------------------------------------------------------------- Page header */
export function PageHeader({ title, description, action, children }) {
  return (
    <div className="mb-6" data-testid="page-header">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">{title}</h1>
          {description && <p className="text-slate-500 mt-1 max-w-2xl">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------- Section card */
export function SectionCard({ title, icon, action, children, className, ...props }) {
  const Icon = icon ? resolveIcon(icon) : null;
  return (
    <div className={cn("bg-white border border-slate-200 rounded-xl shadow-sm", className)} {...props}>
      {(title || action) && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-slate-400" />}
            {title && <h3 className="font-heading font-medium text-slate-800">{title}</h3>}
          </div>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

/* ----------------------------------------------------------------- StatCard */
const ACCENTS = {
  blue: "bg-slate-100 text-slate-700", emerald: "bg-emerald-50 text-emerald-600",
  violet: "bg-amber-50 text-amber-700", amber: "bg-amber-50 text-amber-600",
  sky: "bg-teal-50 text-teal-600", rose: "bg-rose-50 text-rose-600",
  indigo: "bg-slate-100 text-slate-700", teal: "bg-teal-50 text-teal-600",
  slate: "bg-slate-100 text-slate-600",
  gold: "bg-amber-50 text-amber-700", green: "bg-emerald-50 text-emerald-700",
  charcoal: "bg-slate-100 text-slate-800", sage: "bg-emerald-50 text-emerald-700",
};
export function StatCard({ label, value, icon, accent = "slate", testId, trend, onClick, urgent }) {
  const Icon = resolveIcon(icon);
  const TrendIcon = trend?.direction === "up" ? Icons.TrendingUp : trend?.direction === "down" ? Icons.TrendingDown : Icons.Minus;
  const trendColor = trend?.direction === "up" ? "text-emerald-600" : trend?.direction === "down" ? "text-rose-600" : "text-slate-400";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "text-left w-full bg-white border rounded-xl p-5 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-gold/50",
        onClick && "hover:shadow-md hover:-translate-y-0.5 cursor-pointer",
        urgent ? "border-amber-300 ring-1 ring-amber-100" : "border-slate-200"
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            {label}
            {urgent && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" data-testid="urgent-dot" />}
          </div>
          <div className="font-heading text-3xl font-semibold text-slate-900 mt-2 tracking-tight">{value}</div>
          {trend && (
            <div className={cn("flex items-center gap-1 mt-1 text-xs font-medium", trendColor)}>
              <TrendIcon className="h-3.5 w-3.5" />
              {trend.value ? `${trend.value > 0 ? "+" : ""}${trend.value}%` : "No change"}
            </div>
          )}
        </div>
        <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", ACCENTS[accent] || ACCENTS.slate)}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </button>
  );
}

/* -------------------------------------------------- Workflow status pipeline */
export function WorkflowPipeline({ stages = [], currentStage, history = [], percent, onStageClick, catalog = false }) {
  const keys = stages.map((s) => s.key);
  const currentIdx = catalog ? -1 : keys.indexOf(currentStage);
  const histMap = Object.fromEntries(history.map((h) => [h.stage, h]));
  const pct = percent != null ? percent : (currentIdx >= 0 ? Math.round((currentIdx + 1) / keys.length * 100) : 0);

  return (
    <div className="w-full" data-testid="workflow-pipeline">
      {!catalog && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Workflow Progress</span>
          <span className="text-sm font-medium text-slate-700">{pct}% complete</span>
        </div>
      )}
      <div className="flex items-center overflow-x-auto tf-scroll pb-2">
        {stages.map((s, i) => {
          const completed = currentIdx >= 0 && i < currentIdx;
          const current = i === currentIdx;
          const h = histMap[s.key];
          const tip = catalog ? undefined : (h ? `${s.label}\nCompleted: ${new Date(h.completed_at).toLocaleString()}${h.owner ? `\nOwner: ${h.owner}` : ""}` : s.label);
          const clickable = !catalog && (completed || current) && onStageClick;
          return (
            <div key={s.key} className="flex items-center shrink-0">
              <div
                className={cn("flex flex-col items-center gap-1.5", clickable && "cursor-pointer")}
                title={tip}
                onClick={() => clickable && onStageClick(s, h)}
                data-testid={`pipeline-stage-${s.key}`}
              >
                <span className={cn(
                  "h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors",
                  catalog ? "bg-white border-slate-300 text-slate-500"
                    : completed ? "bg-emerald-500 border-emerald-500 text-white"
                    : current ? "bg-brand-gold border-brand-gold text-charcoal ring-4 ring-brand-gold/25"
                    : "bg-white border-slate-300 text-slate-400"
                )}>
                  {completed ? <Icons.Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={cn("text-[11px] whitespace-nowrap max-w-[90px] truncate text-center",
                  catalog ? "text-slate-600"
                    : current ? "text-charcoal font-semibold" : completed ? "text-slate-600" : "text-slate-400")}>
                  {s.label}
                </span>
              </div>
              {i < stages.length - 1 && (
                <div className={cn("h-0.5 w-8 md:w-12 mx-1 mb-5 rounded", (!catalog && i < currentIdx) ? "bg-emerald-500" : "bg-slate-200")} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- StatusBadge */
const TONES = {
  success: "bg-emerald-50 text-emerald-700", info: "bg-blue-50 text-blue-700",
  warning: "bg-amber-50 text-amber-700", danger: "bg-red-50 text-red-700",
  neutral: "bg-slate-100 text-slate-600", purple: "bg-violet-50 text-violet-700",
};
export function Pill({ tone = "neutral", children, className, ...rest }) {
  return <span {...rest} className={cn("inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium", TONES[tone], className)}>{children}</span>;
}
export function StatusBadge({ status, map = {}, labels = {} }) {
  const tone = map[status] || "neutral";
  const label = labels[status] || (status || "").replace(/_/g, " ");
  return <Pill tone={tone} className="capitalize">{label}</Pill>;
}

/* ------------------------------------------------------------- Alert banner */
const ALERT_STYLES = {
  info: "bg-blue-50 border-blue-100 text-blue-800", success: "bg-emerald-50 border-emerald-100 text-emerald-800",
  warning: "bg-amber-50 border-amber-100 text-amber-800", danger: "bg-red-50 border-red-100 text-red-800",
};
const ALERT_ICONS = { info: "Info", success: "CheckCircle2", warning: "AlertTriangle", danger: "AlertCircle" };
export function AlertBanner({ tone = "info", title, children, className, ...rest }) {
  const Icon = resolveIcon(ALERT_ICONS[tone]);
  return (
    <div className={cn("flex items-start gap-3 border rounded-lg px-4 py-3 text-sm", ALERT_STYLES[tone], className)} data-testid={rest["data-testid"] || "alert-banner"} {...(rest["data-testid"] ? {} : {})}>
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div>{title && <div className="font-medium">{title}</div>}{children && <div className="opacity-90">{children}</div>}</div>
    </div>
  );
}

/* -------------------------------------------------------------- Empty state */
export function EmptyState({ icon = "Inbox", title = "Nothing here yet", description, action }) {
  const Icon = resolveIcon(icon);
  return (
    <div className="flex flex-col items-center text-center py-14 px-6" data-testid="empty-state">
      <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="h-7 w-7 text-slate-400" strokeWidth={1.8} />
      </div>
      <h3 className="font-heading text-lg font-semibold text-slate-800">{title}</h3>
      {description && <p className="text-slate-500 mt-1.5 max-w-sm">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ----------------------------------------------------------- Loading states */
export function Spinner({ className }) {
  return <Icons.Loader2 className={cn("h-5 w-5 animate-spin text-brand-gold-dark", className)} />;
}
export function LoadingState({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 gap-3 text-slate-500" data-testid="loading-state">
      <Spinner className="h-7 w-7" /><span className="text-sm">{label}</span>
    </div>
  );
}
export function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div className="p-5 space-y-3" data-testid="table-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="h-4 bg-slate-100 rounded animate-pulse flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------- Search / filter row */
export function SearchBar({ value, onChange, placeholder = "Search…", className, testId = "search-bar" }) {
  return (
    <div className={cn("relative", className)}>
      <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        value={value} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder} data-testid={testId}
        className="w-full bg-white border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
      />
    </div>
  );
}
export function FilterBar({ children, className }) {
  return <div className={cn("flex items-center gap-2 flex-wrap", className)} data-testid="filter-bar">{children}</div>;
}

/* ------------------------------------------------------------ Progress bar */
export function ProgressBar({ value = 0, max = 100, tone = "blue", showLabel = false }) {
  const pct = Math.min(100, Math.round((value / (max || 1)) * 100));
  const colors = { blue: "bg-charcoal", charcoal: "bg-charcoal", gold: "bg-brand-gold", emerald: "bg-emerald-500", green: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500", teal: "bg-teal-600" };
  return (
    <div className="w-full" data-testid="progress-bar">
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", colors[tone] || colors.blue)} style={{ width: `${pct}%` }} />
      </div>
      {showLabel && <div className="text-xs text-slate-500 mt-1">{pct}%</div>}
    </div>
  );
}

/* ------------------------------------------------------------ Timeline item */
export function TimelineItem({ icon = "Activity", title, description, meta, last }) {
  const Icon = resolveIcon(icon);
  return (
    <div className="relative pl-6" data-testid="timeline-item">
      {!last && <span className="absolute left-[7px] top-5 bottom-[-20px] w-px bg-slate-100" />}
      <span className="absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white border-2 border-brand-gold flex items-center justify-center">
        <Icon className="h-2.5 w-2.5 text-brand-gold-dark" />
      </span>
      <div className="text-sm text-slate-800 font-medium">{title}</div>
      {description && <div className="text-xs text-slate-500">{description}</div>}
      {meta && <div className="text-[11px] text-slate-400 mt-0.5">{meta}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- DataTable */
export function DataTable({ columns, rows, renderCell, rowKey = "id", empty, testId = "data-table", onRowClick, getRowClass }) {
  if (!rows || rows.length === 0) {
    return empty || <EmptyState />;
  }
  return (
    <div className="overflow-x-auto tf-scroll">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {columns.map((c) => (
              <th key={c.key} className={cn("px-5 py-3 font-semibold", c.align === "right" && "text-right", c.className)}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[rowKey]} onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn("border-b border-slate-50 hover:bg-slate-50/60", onRowClick && "cursor-pointer", getRowClass?.(row))} data-testid={`row-${row[rowKey]}`}>
              {columns.map((c) => (
                <td key={c.key} className={cn("px-5 py-3", c.align === "right" && "text-right", c.cellClassName)}>
                  {renderCell ? renderCell(row, c) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------------------------------- Right context panel */
export function ContextPanel({ open, onOpenChange, title, children }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="context-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-100">
          <SheetTitle className="font-heading">{title}</SheetTitle>
          <SheetDescription className="sr-only">Contextual details panel</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto tf-scroll p-5">{children}</div>
      </SheetContent>
    </Sheet>
  );
}


/**
 * ClientWorkspaceLink — Finance-wide convention for client navigation from
 * Estimate, Invoice, and other client-owned financial detail pages.
 *
 * Renders "Full Name · CL-XXXXXX" as a clickable link to the client's workspace.
 * Gracefully degrades to plain text when clientId is absent (historical records
 * without a canonical client_id) — never guesses or fuzzy-resolves the client.
 *
 * Usage:
 *   <ClientWorkspaceLink clientId={est.client_id} clientName={est.client?.full_name}
 *                        clientNumber={est.client?.client_number} />
 */
export function ClientWorkspaceLink({ clientId, clientName, clientNumber, className, testId }) {
  const navigate = useNavigate();
  const namePart = clientName || "Unknown client";
  const label = clientNumber ? `${namePart} · ${clientNumber}` : namePart;
  if (!clientId) {
    return (
      <span className={cn("text-sm text-slate-500", className)} data-testid={testId || "client-workspace-link-no-id"}>
        {label}
      </span>
    );
  }
  return (
    <button
      onClick={() => navigate(`/client-workspace/${clientId}`)}
      className={cn(
        "group inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-indigo-700 transition-colors",
        className
      )}
      data-testid={testId || "client-workspace-link"}
    >
      <span>{namePart}</span>
      {clientNumber && <span className="text-slate-400 group-hover:text-indigo-400 transition-colors">· {clientNumber}</span>}
      <Icons.ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-70 transition-opacity text-indigo-500" />
    </button>
  );
}
